import { useMemo } from 'react'
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend
} from 'recharts'
import { TrendingUp, DollarSign, Star, AlertTriangle } from 'lucide-react'

function formatR(n) {
  return `R${(Number(n) || 0).toFixed(2)}`
}

function formatDate(iso) {
  if (!iso) return ''
  const [y, m, d] = iso.split('-')
  return `${d}/${m}`
}

const tooltipStyle = { background: '#1f2937', border: '1px solid #374151', borderRadius: '8px', fontSize: '12px' }

export default function TillAnalytics({ records }) {
  const sorted = useMemo(() =>
    [...records].sort((a, b) => (a.date || '').localeCompare(b.date || '')),
    [records]
  )

  // Summary cards
  const stats = useMemo(() => {
    if (records.length === 0) return { avgBalance: 0, totalCashOut: 0, bestDay: null, largestVariance: 0 }

    const now = new Date()
    const monthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
    const thisMonth = records.filter(r => r.date?.startsWith(monthStr))

    let totalCashOut = 0
    thisMonth.forEach(r => {
      totalCashOut += (r.cashOutItems || []).reduce((s, item) => s + (Number(item.amount) || 0), 0)
    })

    const variances = records.map(r => {
      const co = (r.cashOutItems || []).reduce((s, item) => s + (Number(item.amount) || 0), 0)
      const expected = (Number(r.grandTotal) || 0) - co
      return Math.abs(expected - (Number(r.actualCash) || 0))
    })

    const avgBalance = records.length > 0
      ? records.reduce((s, r) => {
          const co = (r.cashOutItems || []).reduce((sum, item) => sum + (Number(item.amount) || 0), 0)
          return s + ((Number(r.grandTotal) || 0) - co)
        }, 0) / records.length
      : 0

    const bestDay = records.reduce((best, r) =>
      (Number(r.grandTotal) || 0) > (Number(best?.grandTotal) || 0) ? r : best, records[0])

    return {
      avgBalance,
      totalCashOut,
      bestDay,
      largestVariance: Math.max(0, ...variances),
    }
  }, [records])

  // Last 30 days data for line chart
  const last30 = useMemo(() => {
    const cutoff = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0]
    return sorted
      .filter(r => r.date >= cutoff)
      .map(r => {
        const cashOut = (r.cashOutItems || []).reduce((s, item) => s + (Number(item.amount) || 0), 0)
        return {
          date: formatDate(r.date),
          totalSales: Number(r.grandTotal) || 0,
          expectedTill: (Number(r.grandTotal) || 0) - cashOut,
        }
      })
  }, [sorted])

  // Weekly revenue by department (stacked bar)
  const weeklyDept = useMemo(() => {
    const weeks = {}
    for (const r of sorted) {
      if (!r.date) continue
      const d = new Date(r.date)
      const weekStart = new Date(d)
      weekStart.setDate(d.getDate() - d.getDay())
      const key = weekStart.toISOString().split('T')[0]
      if (!weeks[key]) weeks[key] = { week: formatDate(key), kitchen: 0, bakery: 0, tuckShop: 0 }
      weeks[key].kitchen += Number(r.kitchen) || 0
      weeks[key].bakery += Number(r.bakery) || 0
      weeks[key].tuckShop += Number(r.tuckShop) || 0
    }
    return Object.values(weeks).slice(-8)
  }, [sorted])

  // Cash out by type (last 14 days)
  const cashOutByType = useMemo(() => {
    const cutoff = new Date(Date.now() - 14 * 86400000).toISOString().split('T')[0]
    const days = {}
    for (const r of sorted) {
      if (!r.date || r.date < cutoff) continue
      if (!days[r.date]) days[r.date] = { date: formatDate(r.date), Expense: 0, 'Float Change': 0, Payout: 0, Other: 0 }
      for (const item of (r.cashOutItems || [])) {
        const type = item.type || 'Other'
        days[r.date][type] = (days[r.date][type] || 0) + (Number(item.amount) || 0)
      }
    }
    return Object.values(days)
  }, [sorted])

  if (records.length === 0) {
    return <div className="text-center py-16 text-gray-600 text-sm">No data yet. Save till records to see analytics.</div>
  }

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign size={14} className="text-brand-400" />
            <span className="text-xs text-gray-500">Avg Daily Balance</span>
          </div>
          <p className="text-xl font-bold text-white">{formatR(stats.avgBalance)}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={14} className="text-red-400" />
            <span className="text-xs text-gray-500">Cash Out (This Month)</span>
          </div>
          <p className="text-xl font-bold text-white">{formatR(stats.totalCashOut)}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Star size={14} className="text-amber-400" />
            <span className="text-xs text-gray-500">Best Revenue Day</span>
          </div>
          <p className="text-xl font-bold text-white">{formatR(stats.bestDay?.grandTotal)}</p>
          <p className="text-xs text-gray-500">{stats.bestDay?.date || ''}</p>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={14} className="text-red-400" />
            <span className="text-xs text-gray-500">Largest Variance</span>
          </div>
          <p className="text-xl font-bold text-white">{formatR(stats.largestVariance)}</p>
        </div>
      </div>

      {/* Line chart: Sales vs Expected Till */}
      {last30.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-300 mb-3">Daily Sales vs Expected Till (Last 30 Days)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={last30}>
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#6b7280' }} />
              <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="totalSales" stroke="#E8761A" strokeWidth={2} dot={false} name="Total Sales" />
              <Line type="monotone" dataKey="expectedTill" stroke="#10b981" strokeWidth={2} dot={false} name="Expected Till" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Stacked bar: weekly revenue by dept */}
      {weeklyDept.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-300 mb-3">Weekly Revenue by Department</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={weeklyDept}>
              <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#6b7280' }} />
              <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="kitchen" stackId="a" fill="#E8761A" name="Kitchen" radius={[0, 0, 0, 0]} />
              <Bar dataKey="bakery" stackId="a" fill="#f59e0b" name="Bakery" radius={[0, 0, 0, 0]} />
              <Bar dataKey="tuckShop" stackId="a" fill="#6366f1" name="Tuck Shop" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Bar chart: cash out by type */}
      {cashOutByType.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-300 mb-3">Cash Out by Type (Last 14 Days)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={cashOutByType}>
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#6b7280' }} />
              <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Expense" fill="#ef4444" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Float Change" fill="#f59e0b" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Payout" fill="#6366f1" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Other" fill="#6b7280" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}
